import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../components/ui/use-toast';
import { ArrowLeft, Printer, Check } from 'lucide-react';
import { getMenuById } from '../../services/menuService';
import { useRestaurant } from '../../context/RestaurantContext';
import { printPageOrFallback } from '../../lib/printBridge';
import { MENU_DESIGN_TEMPLATES, templateById } from './menuDesignTemplates';

// Menu Design Factory — turn a composed menu into a designed, PRINTABLE menu
// card. Loads the same location-scoped payload as the menu View page, renders
// it through one of five ready-made print templates (picked on the left), and
// prints only the designed sheet — silently via the BOXX Print Bridge when the
// agent is installed, else through the normal print dialog.
export default function MenuDesignFactory() {
  const navigate = useNavigate();
  const { menuId, restaurantId, locationId } = useParams();
  const { restaurant, location } = useRestaurant();
  const { toast } = useToast();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templateId, setTemplateId] = useState(MENU_DESIGN_TEMPLATES[0].id);
  const [printing, setPrinting] = useState(false);
  const sheetRef = useRef(null);
  const menusBase = `/restaurant/${restaurantId}/locations/${locationId}/menus`;

  const loadMenu = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMenuById(restaurantId, locationId, menuId);
      setMenu(data);
    } catch (error) {
      console.error('Error loading menu:', error);
      toast({ title: 'Error', description: 'Failed to load menu', variant: 'destructive' });
      setMenu(null);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, locationId, menuId, toast]);

  useEffect(() => {
    if (restaurantId && locationId && menuId) loadMenu();
  }, [restaurantId, locationId, menuId, loadMenu]);

  const template = templateById(templateId);

  const handlePrint = async () => {
    setPrinting(true);
    try {
      // Print ONLY the designed sheet. Via the bridge this captures the sheet's
      // HTML (inline template CSS rides along); without the agent the @media
      // print rules below make window.print() show just the sheet too.
      const route = await printPageOrFallback({
        rememberKey: 'menu-design-factory',
        target: sheetRef.current,
      });
      if (route === 'bridge') {
        toast({ title: 'Sent to printer', description: `“${menu?.name}” — ${template.name} design.` });
      }
    } finally {
      setPrinting(false);
    }
  };

  const fmtPrice = (p) => {
    const n = parseFloat(p);
    if (!Number.isFinite(n)) return '—';
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading menu...</div>;
  }

  if (!menu || !menu.sections || menu.sections.length === 0) {
    return (
      <div className="p-6 w-full">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(menusBase)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">This menu has nothing to design yet.</p>
            <p className="text-xs text-gray-400 mt-1">
              Add sections and dishes in the menu editor, then come back to pick a design and print it.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const restaurantName = restaurant?.name || 'Restaurant';
  const locationName = location?.name || '';

  return (
    <div className="p-6 w-full mdf-page-root">
      {/* Without the bridge agent, plain window.print() prints just the sheet. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .mdf-print-root, .mdf-print-root * { visibility: visible !important; }
          .mdf-print-root { position: absolute; left: 0; top: 0; width: 100%; margin: 0 !important; box-shadow: none !important; }
          aside, nav, header, footer { display: none !important; }
          @page { margin: 0; }
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`${menusBase}/${menuId}`)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">{menu.name}</h1>
          </div>
        </div>
        <Button onClick={handlePrint} disabled={printing}>
          <Printer className="h-4 w-4 mr-2" />
          {printing ? 'Printing…' : `Print — ${template.name}`}
        </Button>
      </div>

      <div className="flex gap-6 items-start flex-col lg:flex-row">
        {/* Template picker */}
        <div className="w-full lg:w-64 shrink-0 space-y-2">
          {MENU_DESIGN_TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTemplateId(t.id)}
              className={`w-full text-left rounded-lg border p-3 transition-colors ${
                t.id === templateId
                  ? 'border-primary ring-1 ring-primary bg-primary/5'
                  : 'border-border hover:bg-secondary/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm text-foreground">{t.name}</span>
                {t.id === templateId && <Check className="h-4 w-4 text-primary" />}
              </div>
              <div className="flex gap-1 mt-2">
                {t.swatch.map((c) => (
                  <span key={c} className="h-3 w-6 rounded-sm border border-black/10" style={{ background: c }} />
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">{t.blurb}</p>
            </button>
          ))}
        </div>

        {/* Live preview — this exact element is what gets printed */}
        <div className="flex-1 min-w-0 overflow-x-auto">
          <div ref={sheetRef} className={`mdf-print-root mdf-${template.id} shadow-xl mx-auto`} style={{ maxWidth: '210mm' }}>
            {/* Template CSS lives INSIDE the printable element so the bridge's
                HTML capture always carries it. */}
            <style>{template.css}</style>
            <div className="mdf-sheet">
              <header className="mdf-header">
                <div className="mdf-resto">{restaurantName}</div>
                <div className="mdf-menuname">{menu.name}</div>
                {locationName && <div className="mdf-tagline">{locationName}</div>}
              </header>

              <div className="mdf-body">
                {menu.sections.map((section) => (
                  <section key={section.id} className="mdf-section">
                    <h2 className="mdf-sec-title">{section.name}</h2>
                    {(section.items || []).map((item) => {
                      const variants = item.variants || [];
                      const single = variants.length === 1 ? variants[0] : null;
                      return (
                        <div key={item.id} className="mdf-item">
                          <div className="mdf-item-row">
                            <span className="mdf-item-name">
                              {item.name}
                              {(item.is_vegan || item.is_vegetarian) && (
                                <span className="mdf-badge mdf-veg"> {item.is_vegan ? '◆ vegan' : '◆'}</span>
                              )}
                              {Number(item.spicy_level) > 0 && <span className="mdf-badge mdf-spicy"> ▲</span>}
                            </span>
                            <span className="mdf-leader" />
                            {single && <span className="mdf-price">{fmtPrice(single.price)}</span>}
                          </div>
                          {item.description && <p className="mdf-desc">{item.description}</p>}
                          {variants.length > 1 && (
                            <div className="mdf-variants">
                              {variants.map((v) => (
                                <div key={v.id} className="mdf-variant">
                                  <span>{v.name}</span>
                                  <span className="mdf-leader" />
                                  <span className="mdf-price">{fmtPrice(v.price)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </section>
                ))}
              </div>

              <footer className="mdf-footer">
                {restaurantName}
                {locationName ? ` · ${locationName}` : ''}
              </footer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
